import { useWelcomeCouponOffer } from "@/hooks/useWelcomeCouponOffer";
import type { WelcomeCouponOffer } from "@/libs/welcome-coupon/getWelcomeOffer";
import {
  useWelcomeCouponPopupStore,
  useWelcomeCouponSessionStore,
} from "@/store/welcome-coupon-popup-store";
import { useTrans } from "@/utils/useTrans";
import {
  FloatingPopup,
  type PopupSlide,
} from "@/v2-components/ui/floating-popup";
import WelcomeCouponSlide from "@/v2-components/welcome-coupon-slide";
import { useGta5PopupSlides } from "@/v2-components/event/gta5-giveaway-popup";
import { useTactGloveVrchatPopupSlides } from "@/v2-components/event/tactglove-vrchat-popup";
import { useHalloween2026PopupSlides } from "@/v2-components/event/halloween-2026-popup";
import { useExoshockPopupSlides } from "@/v2-components/event/exoshock-popup";

/**
 * 홈의 **떠 있는 팝업 하나** — 여러 캠페인을 슬라이드로 모은다.
 *
 * ## ★왜 만들었나 — 두 팝업이 **겹쳤다** (2026-09-11)
 *
 * 종전엔 `pages/index.tsx` 가 둘을 **나란히** 마운트했다:
 *
 * ```
 *   실측 2026-09-11 (양방향 grep 0건 — 서로를 모른다)
 *     WelcomeCouponPopup   fixed z-50 · laptop:bottom-6 laptop:right-6
 *     Gta5GiveawayPopup    fixed bottom-6 right-6 z-50
 *       → 데스크탑 **완전히 같은 자리 · 같은 z-50**
 *       → 모바일은 GTA5 가 `inset-0` 를 깔아 쿠폰을 덮거나 그 반대
 *
 *   겹치는 모집단  비로그인 + 오퍼 있음 + 홈 + 오늘 안 닫음 ∩ 캠페인 창
 * ```
 *
 * 즉 **비로그인 신규 방문자가 캠페인 기간에 홈에 오면** 둘이 동시에 떴다.
 * `FloatingPopup` 은 애초에 N슬라이드용이므로 거기로 모은다.
 *
 * ## 판정은 여기서 하지 않는다
 *
 * 각 캠페인의 노출 조건은 **원래 있던 곳 그대로**다 — 쿠폰은
 * `useWelcomeCouponOffer`(미니 배지와 공유), GTA5 는 서버가 준 `gta5Open`
 * (랜딩 페이지와 **같은 함수·같은 층**). 이 컴포넌트는 「열린 것들을 모아
 * 슬라이드로 만든다」만 한다.
 *
 * ⚠️ **순서** — GTA5 를 먼저 둔다. 마감이 있는 캠페인이라 기간이 끝나면 사라지고,
 * 쿠폰은 상시다. 종전엔 순서 개념 자체가 없었다(둘이 겹쳐 있었으므로).
 *
 * 할로윈 2026(#6061)은 **맨 앞**이다 — 기간이 가장 짧고 정해진 이벤트라 먼저 보인다(GTA 는 prod 에서 10/18 에 닫혔다).
 *
 * EXOSHOCK 은 **할로윈 바로 뒤**다 — 기간 없는 신제품 슬라이드라 마감 있는 할로윈 다음, 나머지보다 앞(2026-10-29). 한국은 띄우지
 * 않는다(그 파일 docblock).
 *
 * VRChat(#6025)은 **맨 뒤**다 — 기간 없는 상시 슬라이드라 마감 있는 캠페인과 신규
 * 가입 쿠폰을 앞에 둔다.
 *
 * ⚠️ 「오늘 안 보기」는 **현재 슬라이드의 것**만 부른다(`onDismissToday`). 쿠폰의
 * 것은 `dismiss()` + `closePopup()` 이라 #5115 계약(팝업·배지 **두 표면 모두**에
 * 걸린다)이 유지된다.
 */
export default function HomeFloatingPopups({
  gta5Open,
}: {
  /** 서버 판정(`pages/index.tsx` 의 `getServerSideProps`). */
  gta5Open: boolean;
}) {
  const { t } = useTrans(["gnb"]);

  const { popupVisible: couponVisible, offer } = useWelcomeCouponOffer();
  const couponDismiss = useWelcomeCouponPopupStore((s) => s.dismiss);
  const couponClose = useWelcomeCouponSessionStore((s) => s.closePopup);

  /**
   * GTA5 는 **자기 슬라이드를 자기가 만든다** — 저장소·데모 쿼리·마크업이 다 그
   * 파일에 있다. 여기서는 받아서 순서만 정한다.
   */
  const halloweenSlides = useHalloween2026PopupSlides();
  const exoshockSlides = useExoshockPopupSlides();
  const slides: PopupSlide[] = [
    ...halloweenSlides,
    ...exoshockSlides,
    ...useGta5PopupSlides({ open: gta5Open }),
  ];
  const vrchatSlides = useTactGloveVrchatPopupSlides();

  if (couponVisible && offer) {
    slides.push({
      key: "welcome-coupon",
      label: t("New member coupon"),
      /**
       * ⚠️ **둘 다 부른다.** `dismiss()` 는 24시간 저장(localStorage)이고
       * `closePopup()` 은 이 세션에서 닫는 것이다 — 종전 체크박스가 정확히
       * 그 둘을 함께 불렀다(~~`welcome-coupon-popup.tsx`~~ 의 `handleDontShowToday`).
       * 하나만 부르면 배지 쪽 계약이 깨진다.
       */
      onDismissToday: () => {
        couponDismiss();
        couponClose();
      },
      // 셸의 「닫기」도 이번 방문 닫힘으로 반영한다 — 안 그러면 미니 배지 노출 조건
      // (`popupVisible === false`)이 계속 거짓으로 남아 배지가 뜨지 않는다(#5761).
      onClose: couponClose,
      node: <WelcomeCouponSlide offer={offer} />,
    });
  }

  slides.push(...vrchatSlides);

  return <FloatingPopup slides={slides} />;
}
